// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Camera presets (M3-10).
//
// Four poses, all expressed in the world frame the cabinet is built in, all derived from the
// machine's own dimensions rather than from numbers that happen to look right for NEON NOVA.
// A machine with a longer body must not need a second set of cameras.
//
// Pure arithmetic and no Three.js, so the poses can be asserted without a GPU — which matters
// more than it sounds: "the camera is inside the backbox" is invisible to every test that does
// not actually compute where it ended up, and obvious the moment one does.

export const CAMERA_PRESETS = ['player', 'follow', 'overhead', 'free'] as const
export type CameraPreset = (typeof CAMERA_PRESETS)[number]

/**
 * The aspect the presets were framed for.
 *
 * 4:3 — a desktop pane that is wider than it is tall, which is what every `fov` in this file was
 * chosen against.
 */
export const DESIGN_ASPECT = 4 / 3

/**
 * The vertical field of view that keeps the *horizontal* one constant (M3-32).
 *
 * `PerspectiveCamera.fov` is vertical, so a pane that gets narrower shows **less width**, not more
 * height — and a pinball machine is a tall object whose whole width matters. On a phone in
 * portrait the pane is about 0.55 aspect against the 1.33 these poses were framed for, which
 * crops both side rails off the machine and leaves a strip of playfield with no cabinet round it.
 * That reads as "the camera is broken", and it is: the camera is doing exactly what it was told.
 *
 * Above the design aspect nothing changes — a wide pane already has the width, and widening the
 * vertical angle to match would put the ceiling and the floor in frame.
 */
export function fovForAspect(fovDegrees: number, aspect: number, design = DESIGN_ASPECT): number {
  if (!(aspect > 0) || aspect >= design) return fovDegrees
  const vertical = (fovDegrees * Math.PI) / 180
  // The horizontal half-angle the pose was framed with, held fixed and solved back for vertical.
  const horizontal = Math.atan(Math.tan(vertical / 2) * design)
  return (2 * Math.atan(Math.tan(horizontal) / aspect) * 180) / Math.PI
}

/** Clear air to leave round the cabinet when fitting it to a frame, metres. */
export const FIT_MARGIN = 0.06

/**
 * How much of the cabinet's length reads as *height* in the frame.
 *
 * The camera looks down the table at roughly forty degrees, so a 1.3 m cabinet does not project
 * 1.3 m of frame — it foreshortens. Measured off the player pose rather than derived, because
 * deriving it needs the incline, the eye height and the target in one expression and the answer
 * would still be an approximation. Conservative on purpose: too large simply leaves a little more
 * air round the machine, and too small crops the apron.
 */
export const LENGTH_FORESHORTENING = 0.78

/**
 * Pull the eye in until the machine fills a narrow frame (M3-32).
 *
 * `fovForAspect` stops a portrait pane from cropping the machine's width, and it does so by
 * widening the *vertical* angle — which then leaves a tall pane showing a small machine with a
 * band of black above and below it. This spends that room: the eye slides along its own view
 * direction until the cabinet's width fills the horizontal angle **or** its foreshortened length
 * fills the vertical one, whichever runs out first.
 *
 * It only ever pulls **in**. The authored poses are what a desktop should see, and a fit that
 * could also push the camera back would quietly re-frame every preset on every screen — the phone
 * is the case that needs helping, and it is the case where the pane has room going spare.
 */
export function fitEye(
  eye: readonly [number, number, number],
  target: readonly [number, number, number],
  machine: MachineFrame,
  fovDegrees: number,
  aspect: number,
): readonly [number, number, number] {
  if (!(aspect > 0) || aspect >= DESIGN_ASPECT) return eye
  const vHalf = (fovForAspect(fovDegrees, aspect) * Math.PI) / 360
  const hHalf = Math.atan(Math.tan(vHalf) * aspect)
  if (!(vHalf > 0) || !(hHalf > 0)) return eye

  const needWidth = (machine.width / 2 + FIT_MARGIN) / Math.tan(hHalf)
  const needLength =
    ((machine.length * LENGTH_FORESHORTENING) / 2 + FIT_MARGIN) / Math.tan(vHalf)
  const need = Math.max(needWidth, needLength)

  const dx = eye[0] - target[0]
  const dy = eye[1] - target[1]
  const dz = eye[2] - target[2]
  const distance = Math.sqrt(dx * dx + dy * dy + dz * dz)
  if (!(distance > 0) || need >= distance) return eye

  const k = need / distance
  return [target[0] + dx * k, target[1] + dy * k, target[2] + dz * k]
}

/**
 * The lens a roster tile is taken with (M8-82, re-aimed M8-83).
 *
 * **A tile is a photograph of a playfield, and the two earlier answers were each half of one.**
 * M8-81 used the raw player pose, which put a backglass across the top of a 240-pixel frame; M8-82
 * looked straight down the surface normal, which cannot show a ramp — a ramp is 62 mm of height on
 * a 1.07 m table and from directly above it is a line. What a player recognises is the table seen
 * *down its length*: the near edge close and wide, the far end compressed, the wireforms sweeping
 * away. That is `TILE_PITCH` below.
 *
 * The field is narrow because the camera is outside the cabinet looking in, and a wide angle at
 * that distance bows the side rails outward like a fisheye.
 */
export const TILE_FOV = 38

/**
 * How far above the playfield's own plane the tile camera sits, degrees.
 *
 * **Not the player's 22°.** A real player stands at the machine and sees the table at a glancing
 * angle, which is right for playing — the ball is what matters and it is on the wood — and wrong
 * for a thumbnail, where the far third of the playfield collapses into a band a few pixels tall.
 * Steeper opens the layout out; steeper still is M8-82's overhead, which flattens the ramps.
 *
 * 47° is read off the picture the player asked for rather than chosen: at that pitch this table
 * projects into a frame of aspect 0.944, and the screenshot they supplied is 0.92. It is also what
 * sets `ATTRACT_WIDTH / ATTRACT_HEIGHT` — see `tileAspect`, and note which way round that
 * dependency runs. Forcing this view into the old 0.571 tile is what left a band of air across the
 * top with the backbox in it: probed across 40°…70°, the vertical fill never once reached the
 * horizontal one at that shape, because a narrower frame only pushes the camera further back.
 */
export const TILE_PITCH = 47

/** Clear air to leave round the playfield in a tile, as a fraction of the frame. */
export const TILE_MARGIN = 0.03

/**
 * Where the tile camera goes, in the playfield's **own** frame.
 *
 * `x` across the table, `y` up it towards the backbox, `z` out of the surface — so the answer is
 * independent of where the cabinet stands, what it is inclined at and which way it faces, and the
 * caller turns it into world space with the one matrix that already knows all three.
 *
 * **The distance is fitted by bisection rather than solved.** A rectangle seen obliquely does not
 * project to a rectangle, so "the whole playfield is in frame" has no closed form worth writing:
 * the binding corner swaps between the near and far pair as the pitch changes, and the near pair
 * can leave through the *side* while the far pair is still comfortably inside. Forty halvings of a
 * bracket is exact to well under a pixel, runs in microseconds, and — the part that matters — is
 * pure arithmetic, so `attract-parts.test.ts` can assert every corner of both shipped machines is
 * inside the frustum without a GPU anywhere.
 *
 * This is what M8-81 reached for and got wrong. It applied `fitEye`, which fits the **cabinet** —
 * so the picture came back with the legs, the floor and a ring of empty room around a machine seen
 * from across it. Fitting the *playfield* is the same idea aimed at the thing the tile is of.
 */
interface TileFit {
  readonly eye: readonly [number, number, number]
  readonly target: readonly [number, number, number]
  /** How much of the frame the playfield uses, 1 being hard against the edge. */
  readonly across: number
  readonly up: number
}

/**
 * The fitted pose and what it fills, for one frame shape.
 *
 * Private because the two exports want different halves of it, and computing it twice would be
 * two chances for them to disagree about the same picture.
 */
function fitTile(
  playfield: { readonly width: number; readonly length: number },
  aspect: number,
  fovDegrees: number,
  pitchDegrees: number,
  margin: number,
): TileFit {
  const vHalf = (fovDegrees * Math.PI) / 360
  if (!(aspect > 0) || !(vHalf > 0) || vHalf >= Math.PI / 2 || !(playfield.length > 0)) {
    return { eye: [0, 0, 1], target: [0, 0, 0], across: 0, up: 0 }
  }
  // Shrunk rather than the playfield grown, so the margin is a fraction of the *frame* and a
  // machine of any size keeps the same border.
  const keep = Math.max(0.01, 1 - margin * 2)
  const tanV = Math.tan(vHalf)
  const tanH = tanV * aspect

  const pitch = (pitchDegrees * Math.PI) / 180
  const dir: readonly [number, number, number] = [0, -Math.cos(pitch), Math.sin(pitch)]
  const halfW = playfield.width / 2
  const halfL = playfield.length / 2
  const corners: readonly (readonly [number, number])[] = [
    [-halfW, -halfL],
    [halfW, -halfL],
    [-halfW, halfL],
    [halfW, halfL],
  ]

  // The view basis. It depends only on the pitch, so it is built once rather than per probe.
  const f: readonly [number, number, number] = [-dir[0], -dir[1], -dir[2]]
  const r: readonly [number, number, number] = [1, 0, 0]
  const u: readonly [number, number, number] = [
    r[1] * f[2] - r[2] * f[1],
    r[2] * f[0] - r[0] * f[2],
    r[0] * f[1] - r[1] * f[0],
  ]

  /** Where the playfield's corners land, in fractions of the frame, at one distance and aim. */
  const seenAt = (
    distance: number,
    aimY: number,
  ): { across: number; low: number; high: number; ahead: boolean } => {
    const eye = [dir[0] * distance, aimY + dir[1] * distance, dir[2] * distance] as const
    let across = 0
    let low = Number.POSITIVE_INFINITY
    let high = Number.NEGATIVE_INFINITY
    for (const [cx, cy] of corners) {
      const v = [cx - eye[0], cy - eye[1], -eye[2]] as const
      const depth = v[0] * f[0] + v[1] * f[1] + v[2] * f[2]
      if (!(depth > 0)) return { across: 0, low: 0, high: 0, ahead: false }
      const x = (v[0] * r[0] + v[1] * r[1] + v[2] * r[2]) / (depth * tanH)
      const y = (v[0] * u[0] + v[1] * u[1] + v[2] * u[2]) / (depth * tanV)
      across = Math.max(across, Math.abs(x))
      low = Math.min(low, y)
      high = Math.max(high, y)
    }
    return { across, low, high, ahead: true }
  }

  /**
   * The aim that puts the picture in the middle of the frame.
   *
   * **Without this the fit is tight and the composition is not.** A table's near edge is much
   * closer to the eye than its far edge, so it projects far larger and hangs below the middle:
   * aiming at the geometric centre put NEON NOVA's near corners hard against the bottom of the
   * frame with the far end at 52% of the way up, and the band of air that left at the top was
   * filled by exactly the thing this view exists to keep out — the backbox.
   *
   * So the aim slides **towards the near edge**, which tips the camera down and lifts the picture.
   * That direction is worth stating because it is the opposite of the intuitive one, and the first
   * version of this comment had it backwards; the bisection was right and the sentence was not.
   */
  const centred = (distance: number): number => {
    let low = -playfield.length
    let high = playfield.length
    for (let i = 0; i < 40; i++) {
      const middle = (low + high) / 2
      const seen = seenAt(distance, middle)
      if (!seen.ahead || seen.low + seen.high < 0) high = middle
      else low = middle
    }
    return (low + high) / 2
  }

  const fits = (distance: number): boolean => {
    const seen = seenAt(distance, centred(distance))
    return seen.ahead && seen.across <= keep && seen.low >= -keep && seen.high <= keep
  }

  // A bracket wide enough for any machine the format allows: a playfield's length is validated
  // into 0.4…2.0 m, and a table cannot need more than a few of its own lengths of stand-off.
  let near = 0
  let far = Math.max(4, playfield.length * 6)
  if (fits(far)) {
    for (let i = 0; i < 40; i++) {
      const middle = (near + far) / 2
      if (fits(middle)) far = middle
      else near = middle
    }
  }
  const aimY = centred(far)
  const seen = seenAt(far, aimY)
  return {
    eye: [dir[0] * far, aimY + dir[1] * far, dir[2] * far],
    target: [0, aimY, 0],
    across: seen.across / keep,
    up: Math.max(Math.abs(seen.low), Math.abs(seen.high)) / keep,
  }
}

export function tileView(
  playfield: { readonly width: number; readonly length: number },
  aspect: number,
  fovDegrees: number = TILE_FOV,
  pitchDegrees: number = TILE_PITCH,
  margin: number = TILE_MARGIN,
): { readonly eye: readonly [number, number, number]; readonly target: readonly [number, number, number] } {
  const fit = fitTile(playfield, aspect, fovDegrees, pitchDegrees, margin)
  return { eye: fit.eye, target: fit.target }
}

/**
 * The frame shape this view wants, for a playfield of these proportions.
 *
 * **Read rather than chosen, and measured rather than solved.** A table seen down its length does
 * not project to its own proportions — the near edge is nearer, so it is wider — and my first
 * attempt at this was the closed form `width / (length · sin pitch)`, which is the answer for a
 * camera aimed at the geometric centre. The aim moves, so that was wrong by a quarter: it said
 * 0.72 for NEON NOVA where the fit actually binds at 0.94. Bisecting the real fit costs
 * microseconds and cannot drift from it.
 *
 * This is where `ATTRACT_WIDTH / ATTRACT_HEIGHT` came from. Forcing the view into the old 0.571
 * frame is what left the band of air a backbox then occupied — and no distance could have fixed
 * it, because a narrower frame only pushes the camera further back.
 */
export function tileAspect(
  playfield: { readonly width: number; readonly length: number },
  pitchDegrees: number = TILE_PITCH,
): number {
  let low = 0.2
  let high = 3
  for (let i = 0; i < 40; i++) {
    const middle = (low + high) / 2
    const fit = fitTile(playfield, middle, TILE_FOV, pitchDegrees, TILE_MARGIN)
    // A wider frame brings the camera in, which fills more of the height. Below the crossing the
    // width binds and there is air above; above it, the reverse.
    if (fit.up < fit.across) low = middle
    else high = middle
  }
  return (low + high) / 2
}

export interface CameraPose {
  readonly position: readonly [number, number, number]
  readonly target: readonly [number, number, number]
  /** Vertical field of view, degrees. */
  readonly fov: number
}

export interface MachineFrame {
  /** Cabinet length front-to-back and width, metres. */
  readonly length: number
  readonly width: number
  /** Floor to the top of the lockdown bar. */
  readonly lockdownBarHeight: number
  /** Playfield rise from front to back, metres. */
  readonly rise: number
}

/**
 * Where the camera goes for a preset.
 *
 * `focus` is what the Follow camera should look at — the ball, in world space. Every other
 * preset ignores it, and Follow falls back to the playfield centre when there is no ball,
 * because a camera that snaps to the origin the instant a ball drains is worse than one that
 * simply holds still.
 */
export function cameraPose(
  preset: CameraPreset,
  machine: MachineFrame,
  focus?: readonly [number, number, number],
): CameraPose {
  const halfLength = machine.length / 2
  const frontTop = machine.lockdownBarHeight
  const centreY = frontTop + machine.rise / 2
  const centre: readonly [number, number, number] = [0, centreY, 0]

  // The player's own pose, which Follow is a small deviation from rather than a different idea.
  const playerEye: readonly [number, number, number] = [0, 1.62, halfLength + 0.42]
  const playerTarget: readonly [number, number, number] = [
    0,
    frontTop + machine.rise * 0.35,
    -halfLength * 0.25,
  ]

  switch (preset) {
    case 'player':
      // Standing at the machine: eye height, behind the lockdown bar, looking up the playfield.
      // 1.62 m is a median standing eye height, and it is the pose every other preset is judged
      // against because it is the one a real player has.
      return { position: playerEye, target: playerTarget, fov: 52 }

    case 'follow': {
      // 08 §4.1: "**Player view** with a gentle ball-tracking pan and a slight dolly toward the
      // action; the default on phones."
      //
      // It used to be a chase camera thirty centimetres behind the ball, and that is a different
      // idea wearing the same name. Two things were wrong with it and the second is disqualifying:
      // every slingshot became a whip pan, and — because the camera sat *at* the ball — **the
      // flippers left the frame the moment the ball did**. A phone defaults to this preset, so
      // the effect was a machine you could not see your own bats on, which is not a camera
      // preference but an unplayable game.
      //
      // A pan and a dolly, then. `PAN` is how much of the ball's offset the aim follows and
      // `DOLLY` is how far the eye leans in after it; both are small on purpose, because the
      // frame has to keep the bats and the shooter lane in it at every ball position on the
      // playfield. With no ball it is exactly the player's view, which is the right thing to
      // hold during attract and between balls.
      const PAN = 0.35
      const DOLLY = 0.12
      if (focus === undefined) return { position: playerEye, target: playerTarget, fov: 48 }
      return {
        position: [
          playerEye[0] + (focus[0] - playerTarget[0]) * DOLLY,
          playerEye[1] + (focus[1] - playerTarget[1]) * DOLLY,
          playerEye[2] + (focus[2] - playerTarget[2]) * DOLLY,
        ],
        target: [
          playerTarget[0] + (focus[0] - playerTarget[0]) * PAN,
          playerTarget[1] + (focus[1] - playerTarget[1]) * PAN,
          playerTarget[2] + (focus[2] - playerTarget[2]) * PAN,
        ],
        fov: 48,
      }
    }

    case 'overhead':
      // Straight down, framed on the playfield. The diagnostic view, and the one that matches
      // the 2-D harness, so the two can be compared without mentally rotating anything.
      return {
        position: [0, centreY + machine.length * 0.86, 0.001],
        target: centre,
        fov: 50,
      }

    case 'free':
      // The starting pose for a free orbit: three-quarters from the player's left, high enough
      // to show the cabinet as an object rather than as a surface.
      return {
        position: [-machine.width * 1.15, frontTop + 0.72, halfLength + 0.66],
        target: centre,
        fov: 48,
      }
  }
}

/**
 * Keep a free-orbit camera out of the floor and out of the machine.
 *
 * Clamped rather than free (M3-10): an unclamped orbit's first discovery is always the inside
 * of the cabinet, and its second is the underside of the playfield, and neither is a view of a
 * pinball machine.
 */
export function clampOrbit(
  azimuth: number,
  elevation: number,
  distance: number,
): { azimuth: number; elevation: number; distance: number } {
  const TWO_PI = Math.PI * 2
  let a = azimuth % TWO_PI
  if (a < 0) a += TWO_PI
  return {
    azimuth: a,
    // Never below the lockdown bar's plane and never straight down: at exactly 90° the up
    // vector and the view direction are parallel and the view rolls unpredictably.
    elevation: Math.min(Math.max(elevation, 0.08), 1.48),
    distance: Math.min(Math.max(distance, 0.55), 4),
  }
}

/** Where a free orbit is: the three numbers a drag and a wheel move. */
export interface Orbit {
  azimuth: number
  elevation: number
  distance: number
}

/**
 * The inverse of `orbitPosition`: read an orbit off a pose.
 *
 * This is what lets the Free camera *start* exactly where its authored pose put it. Without it
 * the first drag would snap the view to whatever the orbit happened to be initialised to, and
 * a camera that jumps the instant you touch it reads as broken however good the pose was.
 *
 * Not clamped. The authored pose is the designer's, and silently moving it would hide a pose
 * that is actually outside the playable orbit rather than reporting one.
 */
export function orbitFrom(
  position: readonly [number, number, number],
  target: readonly [number, number, number],
): Orbit {
  const dx = position[0] - target[0]
  const dy = position[1] - target[1]
  const dz = position[2] - target[2]
  const distance = Math.sqrt(dx * dx + dy * dy + dz * dz)
  if (distance === 0) return { azimuth: 0, elevation: 0, distance: 0 }
  // `asin` rather than `atan2(dy, horizontal)`, so a camera directly above its target gives
  // exactly π/2 instead of depending on which way two zeros round.
  const elevation = Math.asin(Math.min(Math.max(dy / distance, -1), 1))
  // `atan2` answers in (−π, π] and `clampOrbit` normalises to [0, 2π). One convention for
  // azimuth across the module, or the two disagree by a full turn for half of all poses and
  // every comparison between them is right only by accident.
  const TWO_PI = Math.PI * 2
  let azimuth = Math.atan2(dx, dz) % TWO_PI
  if (azimuth < 0) azimuth += TWO_PI
  return { azimuth, elevation, distance }
}

/** An orbit angle triple resolved to a world position around a target. */
export function orbitPosition(
  target: readonly [number, number, number],
  azimuth: number,
  elevation: number,
  distance: number,
): readonly [number, number, number] {
  const c = Math.cos(elevation)
  return [
    target[0] + Math.sin(azimuth) * c * distance,
    target[1] + Math.sin(elevation) * distance,
    target[2] + Math.cos(azimuth) * c * distance,
  ]
}
